import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, forkJoin, of } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { HelpConfig } from "../config/help-config";
import { FEATURE_FLAGS, FeatureFlagName } from "./feature-flags.const";

@Injectable({ providedIn: "root" })
export class FeatureFlagService {
  private readonly url = `${this.helpConfig.BASE_API}parameter/get`;
  private readonly _flags$ = new BehaviorSubject<Record<string, boolean>>({});
  private loaded = false;

  /** Emite a cada (re)carga; use para recalcular menus e telas. */
  readonly flags$ = this._flags$.asObservable();

  constructor(private http: HttpClient, private helpConfig: HelpConfig) {}

  /**
   * Carrega as flags via `GET parameter/get/{chave}` (chaves da whitelist da API liberam não-admin).
   * Erro, linha ausente ou qualquer valor diferente de "false" = ligada, como no backend.
   */
  load(): Observable<Record<string, boolean>> {
    const keys = Object.values(FEATURE_FLAGS).map((f) => f.key);
    return forkJoin(
      keys.map((key) =>
        this.http.get(`${this.url}/${key}`, { responseType: "text" }).pipe(
          map((value) => this.parse(value)),
          catchError(() => of(true))
        )
      )
    ).pipe(
      map((enabled) => Object.fromEntries(keys.map((key, i) => [key, enabled[i]]))),
      tap((flags) => {
        this.loaded = true;
        this._flags$.next(flags);
      })
    );
  }

  /** A API serializa a string em JSON (`"false"` com aspas); aceita também texto puro. */
  private parse(raw: string): boolean {
    const value = (raw ?? "").trim().replace(/^"|"$/g, "").trim().toLowerCase();
    return value !== "false";
  }

  /** Para guards: usa o cache se já carregou, senão carrega antes de decidir. */
  ensureLoaded(): Observable<boolean> {
    return this.loaded ? of(true) : this.load().pipe(map(() => true));
  }

  refresh(): void {
    this.load().subscribe();
  }

  isEnabled(flag: FeatureFlagName): boolean {
    return this._flags$.value[FEATURE_FLAGS[flag].key] !== false;
  }

  isEnabled$(flag: FeatureFlagName): Observable<boolean> {
    return this.flags$.pipe(map(() => this.isEnabled(flag)));
  }

  /** Perfis vêm do `localStorage.res.profiles` (ex.: AdminRenova), não do JWT. */
  isAdmin(): boolean {
    try {
      const profiles = JSON.parse(localStorage.getItem("res") ?? "null")?.profiles;
      return Array.isArray(profiles) && profiles.includes("AdminRenova");
    } catch {
      return false;
    }
  }
}
